<template>
  <div
    :id="tableActionsAppendToSelectors.attribute"
    :class="{ [`df-DescriptionField-${styleType}`]: true }"
    class="df-DescriptionField"
    tabindex="0"
  >
    <div ref="editorRef" :class="editorClasses">
      <div
        v-if="editor"
        v-show="active"
        :class="{
          'df-Toolbar-sticky': stickyToolbar
        }"
        class="df-Toolbar"
      >
        <AppSelect
          :append-to="getAppendToElementClass"
          :class="{ 'df-TextFormats-short': shortNameTextFormat }"
          :dropdown-min-width="250"
          :model-value="textFormat"
          :offset="[0, 0]"
          :options="textFormats"
          class="df-TextFormats"
        >
          <template #button="{ option, active: TextFormatActive }">
            <div
              :class="{ 'df-TextFormats_DroplistButton-active': TextFormatActive }"
              class="df-TextFormats_DroplistButton"
            >
              <span class="df-DroplistButton_Text">
                {{ shortNameTextFormat ? option.shortLabel : option.label }}
              </span>
              <AppIcon
                class="df-DroplistButton_Icon"
                height="24"
                icon-name="arrow-down-black"
                width="24"
              />
            </div>
          </template>
          <template #option="{ option, onSelect, selected }">
            <div
              :class="{
                [`df-TextFormats_Heading-${option.value}`]: option.value,
                'df-TextFormats_Heading-active': selected
              }"
              class="df-TextFormats_Heading"
              @click="setTextFormat(option, onSelect)"
            >
              {{ option.label }}
              <ShortcutItem :item="option" />
            </div>
          </template>
        </AppSelect>

        <div class="df-ToolbarSplitter" />

        <DescriptionButton
          v-tippy="{
            content: convertShortcutToText(getCurrentTip(TIPTAP_EXTENSION_NAMES.BOLD)),
            placement: 'top'
          }"
          :active="editor.isActive('bold')"
          icon="format-bold"
          @click="tiptapChainFocusFnc(editor).toggleBold().run()"
        />

        <DescriptionButton
          v-tippy="{
            content: convertShortcutToText(getCurrentTip(TIPTAP_EXTENSION_NAMES.ITALICIZE)),
            placement: 'top'
          }"
          :active="editor.isActive('italic')"
          icon="format-italic"
          @click="tiptapChainFocusFnc(editor).toggleItalic().run()"
        />

        <AppSelect
          ref="textColorPicker"
          :append-to="getAppendToElementClass"
          :offset="[0, 0]"
          :options="COLORS"
          content-class="df-ColorText_ContentOptions"
          dropdown-width="260px"
        >
          <template #button="{ active: activeTextColorPicker }">
            <div class="df-ColorTextWrapper">
              <DescriptionButton
                :active="activeTextColorPicker || editor.isActive('textStyle')"
                icon="format-color-picker"
                icon-height="24"
                icon-width="32"
                size="md"
              />
              <div
                :style="{
                  background: getColorAttribute('textStyle')
                }"
                class="df-ColorTextWrapper_Marker"
              />
            </div>
          </template>
          <template #option="{ option }">
            <div class="df-ColorTextWrapper_OptionWrapper">
              <div
                :class="{
                  'df-ColorTextWrapper_Option-active': editor.isActive('textStyle', {
                    color: option
                  })
                }"
                :style="{ 'background-color': option }"
                class="df-ColorTextWrapper_Option"
                @click="onTextColorChange(option)"
              >
                <AppIcon
                  v-if="editor.isActive('textStyle', { color: option })"
                  :style="{ color: ICON_COLORS[option] }"
                  height="22"
                  icon-name="check"
                  width="22"
                />
              </div>
            </div>
          </template>
        </AppSelect>

        <AppSelect
          :append-to="getAppendToElementClass"
          :dropdown-min-width="220"
          :group-by="item => item.group"
          :model-value="moreFormat"
          :offset="[0, 0]"
          :options="moreFormats"
          :show-group-header="false"
          max-height="240"
          show-group-divider
        >
          <template #option="{ option, onSelect }">
            <!-- eslint-disable vue/no-v-html -->
            <div
              :class="{
                'df-TextFormats_Heading-active': editor.isActive(option.value.toLowerCase())
              }"
              class="df-TextFormats_Heading"
              @click="onMoreFormatChange(option, onSelect)"
            >
              <span>
                <template v-if="option.labelDescription">
                  {{ $t(option.label) }}
                  <i18n-t :keypath="option.labelDescription" :tag="option.tag" scope="global" />
                </template>
                <i18n-t v-else :keypath="option.label" :tag="option.tag" scope="global" />
              </span>
              <ShortcutItem :item="option" />
            </div>
          </template>
          <template #button="{ active: activeMoreFormats }">
            <DescriptionButton
              :active="activeMoreFormats || isActiveAnyMoreFormats"
              icon="format-more"
            />
          </template>
        </AppSelect>

        <div class="df-ToolbarSplitter" />

        <DescriptionButton
          v-tippy="{
            content: convertShortcutToText(getCurrentTip(TIPTAP_EXTENSION_NAMES.BULLET_LIST)),
            placement: 'top'
          }"
          :active="editor.isActive('bulletList')"
          icon="list_bulleted"
          @click="tiptapChainFocusFnc(editor).toggleBulletList().run()"
        />

        <DescriptionButton
          v-tippy="{
            content: convertShortcutToText(getCurrentTip(TIPTAP_EXTENSION_NAMES.ORDER_LIST)),
            placement: 'top'
          }"
          :active="editor.isActive('orderedList')"
          icon="list_numbered"
          @click="tiptapChainFocusFnc(editor).toggleOrderedList().run()"
        />

        <div class="df-ToolbarSplitter" />

        <!--    forbidden to insert table when another table is active   -->
        <template v-if="!simpleView">
          <DescriptionButton
            v-tippy="{
              content: convertShortcutToText(getCurrentTip(TIPTAP_EXTENSION_NAMES.TABLE)),
              placement: 'top'
            }"
            :disable="editor.isActive('table')"
            icon="table-icon"
            @click="insertTable"
          />

          <AppDroplist
            v-model="showUrlModal"
            :append-to="getAppendToElementClass"
            :offset="[0, 10]"
            :theme="DROP_LIST_THEMES.LIGHT"
            has-fixed-parent
            max-width="595"
            position="bottom"
          >
            <template #button>
              <DescriptionButton :active="showUrlModal || editor.isActive('link')" icon="link" />
            </template>
            <div class="df-Toolbar_LinkWrapper">
              <FormFieldNext :label="$t('field.display_text')">
                <AppInput
                  v-model.trim="urlText"
                  :is-error="urlTextError"
                  :max-length="2048"
                  :placeholder="$t('action.paste_text')"
                  class="df-LinkWrapper_Input"
                  icon="cf-long-text"
                  size="xlg"
                  style-type="secondary"
                  @focus="urlTextError = false"
                />
              </FormFieldNext>
              <FormFieldNext :label="$t('custom_fields.link')">
                <AppInput
                  v-model.trim="url"
                  :is-error="urlError"
                  :max-length="2048"
                  :placeholder="$t('action.paste_link')"
                  class="df-LinkWrapper_Input"
                  icon="link"
                  size="xlg"
                  style-type="secondary"
                  @focus="urlError = false"
                />
              </FormFieldNext>
              <AppButton
                class="df-LinkWrapper_Button"
                size="lg-next"
                type="primary-next"
                @click="onConfirmSetUrl"
              >
                {{ $t('action.add_link') }}
              </AppButton>
            </div>
          </AppDroplist>

          <AppEmojiPicker @select-emoji="insertEmoji" />
          <div class="df-ToolbarSplitter" />
        </template>
        <AddGifsDroplist
          v-if="isHidden(TIPTAP_EXTENSION_NAMES.GIFS)"
          ref="gifDroplistRef"
          :editor="editor"
          :placement="placement"
        />
        <AppDroplist
          v-model="showAddYouTubeModal"
          :append-to="getAppendToElementClass"
          :offset="[0, 10]"
          :theme="DROP_LIST_THEMES.LIGHT"
          has-fixed-parent
          max-width="595"
          position="bottom"
        >
          <div class="df-Toolbar_LinkWrapper">
            <AppInput
              v-model="url"
              :placeholder="$t('action.paste_yt_video_link')"
              class="df-LinkWrapper_Input"
              icon="link"
              size="xlg"
              style-type="secondary"
            />
            <AppButton
              class="df-LinkWrapper_Button"
              size="lg-next"
              type="primary-next"
              @click="onConfirmAddYouTubeUrl"
            >
              {{ $t('action.add_video') }}
            </AppButton>
          </div>
        </AppDroplist>

        <AppSelect
          :append-to="getAppendToElementClass"
          :dropdown-min-width="250"
          :offset="[0, 0]"
          :options="simpleViewList"
          position="bottom-end"
        >
          <template #option="{ option, onSelect }">
            <div
              :class="{
                'df-TextFormats_Heading-active': option.isActive,
                'df-TextFormats_Heading-disabled': option.disabled
              }"
              class="df-TextFormats_Heading df-SimpleViewList"
              @click="
                () => {
                  execAction(option.action)
                  onSelect(option)
                }
              "
            >
              <span class="df-SimpleViewList">
                <AppIcon
                  :icon-name="option.icon"
                  class="df-SimpleViewList_Icon"
                  height="24"
                  width="24"
                />
                <span class="oboard-truncated-text">
                  {{ option.title }}
                </span>
              </span>
              <ShortcutItem :item="option" />
            </div>
          </template>
          <template #button="{ active: activeSimpleView }">
            <DescriptionButton
              :active="activeSimpleView"
              icon="add-more"
              icon-height="24"
              icon-width="32"
              size="md"
            />
          </template>
        </AppSelect>
        <!--   This and next row needs for displaying droplist content in the simple view     -->

        <AppDroplist
          v-if="simpleView"
          v-model="showUrlModal"
          :append-to="getAppendToElementClass"
          :offset="[0, 10]"
          :theme="DROP_LIST_THEMES.LIGHT"
          has-fixed-parent
          max-width="595"
          position="bottom"
        >
          <template #button>
            <div />
          </template>
          <div class="df-Toolbar_LinkWrapper">
            <FormFieldNext :label="$t('field.display_text')">
              <AppInput
                v-model.trim="urlText"
                :is-error="urlTextError"
                :max-length="2048"
                :placeholder="$t('action.paste_text')"
                class="df-LinkWrapper_Input"
                icon="cf-long-text"
                size="xlg"
                style-type="secondary"
                @focus="urlTextError = false"
              />
            </FormFieldNext>
            <FormFieldNext :label="$t('custom_fields.link')">
              <AppInput
                v-model.trim="url"
                :is-error="urlError"
                :max-length="2048"
                :placeholder="$t('action.paste_link')"
                class="df-LinkWrapper_Input"
                icon="link"
                size="xlg"
                style-type="secondary"
                @focus="urlError = false"
              />
            </FormFieldNext>
            <AppButton
              class="df-LinkWrapper_Button"
              size="lg-next"
              type="primary-next"
              @click="onConfirmSetUrl"
            >
              {{ $t('action.add_link') }}
            </AppButton>
          </div>
        </AppDroplist>
        <AppEmojiPicker
          v-if="simpleView"
          ref="emojiPickerRef"
          :show-trigger-button="!simpleView"
          @select-emoji="insertEmoji"
        />

        <slot name="toolbar-append" />

        <div v-if="actions && moveActionsToToolbar" class="df-EditorActions">
          <portal-target :name="`editor-actions-${uniqActionsUid}`" />
        </div>
      </div>

      <AppDivider v-if="showToolbarDivider" no-margin />

      <div
        v-if="!active && isDescriptionEmpty && showPlaceholder"
        class="df-Placeholder"
        @click="showEditor"
      >
        <span class="df-Placeholder_Text">
          <slot :placeholder="placeholder" name="placeholder-content">
            {{ placeholder }}
          </slot>
        </span>
      </div>

      <EditorContent
        v-if="active || !isDescriptionEmpty"
        :editor="editor"
        class="df-Content"
        @click="onClickDescriptionField"
      />
    </div>
    <ImageBlockMenu
      v-if="editor && isHidden(TIPTAP_EXTENSION_NAMES.IMAGE_BLOCK)"
      :active="active"
      :editor="editor"
    />

    <div v-if="active" class="df-ActionsAndCounter">
      <portal
        :disabled="!moveActionsToToolbar"
        :to="`editor-actions-${uniqActionsUid}`"
        name="toolbar-append"
      >
        <div v-if="actions" class="df-Actions">
          <div class="df-Actions_Item">
            <AppButton
              :disable="(!allowEmpty && isDescriptionEmpty) || !isDescriptionValid || disable"
              type="primary-next"
              @click="onSave"
            >
              {{ $t('action.save') }}
            </AppButton>
          </div>
          <div class="df-Actions_Item">
            <AppButton type="ghost-next" @click="onCancel">
              {{ $t('action.cancel') }}
            </AppButton>
          </div>
        </div>
        <div
          v-if="isShowCharactersCounter"
          :class="{
            'df-DescriptionCounter': true,
            'df-DescriptionCounter-negative': !isDescriptionValid
          }"
        >
          {{ charactersKept }}
        </div>
      </portal>
    </div>
  </div>
</template>

<script>
// Do not update tiptap, because in next versions it has some performance problems. Current versions work without bugs (we didn't find :)

import CharacterCount from '@tiptap/extension-character-count'
import { Color } from '@tiptap/extension-color'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import TableRow from '@tiptap/extension-table-row'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import Youtube from '@tiptap/extension-youtube'
import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3'
import { isEmpty } from 'lodash'
import { Portal, PortalTarget } from 'portal-vue'
import tippy from 'tippy.js'
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'

import UsersApi from '@/api/workspace-users'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { handleError } from '@/utils/error-handling'
import { createHtmlElementId, isStringEmpty } from '@/utils/general'
import { descriptionFieldStateInjectionKey } from '@/utils/injection-keys'
import { removeKeydownEventListener, unshiftKeydownEventListener } from '@/utils/keyevents'
import { includesValue } from '@/utils/prop-validators'
import { DESCRIPTION_FILED_LIMITS, getIdsInMentions, resolveMentions } from '@/utils/rich-edit'
import {
  COLORS,
  convertShortcutToText,
  DESCRIPTION_FIELD_PLACEMENTS,
  DESCRIPTION_FIELD_TYPES,
  getCurrentTip,
  getMoreFormats,
  getSimpleViewList,
  HEADING_LEVELS,
  ICON_COLORS,
  insertBlockOnNewLine,
  insertTable,
  MENU_LIST,
  MORE_FORMATS_ACTIONS,
  TEXT_FORMATS,
  TIPTAP_EXTENSION_NAMES,
  tiptapChainFocusFnc,
  YOUTUBE_VIDEO_HEIGHT,
  YOUTUBE_VIDEO_WIDTH
} from '@/utils/tiptap-utils'
import { uid } from '@/utils/uid'

import AppDroplist from '@/components/AppDroplist'
import AppEmojiPicker from '@/components/AppEmojiPicker'
import FormFieldNext from '@/components/form/FormFieldNext'
import DescriptionButton from '@/components/objectives/forms/DescriptionButton'
import AddGifsDroplist from '@/components/TipTapExtensions/AddGifs/AddGifsDroplist'
import ImageBlock from '@/components/TipTapExtensions/ImageBlock/image-block'
import ImageBlockMenu from '@/components/TipTapExtensions/ImageBlock/ImageBlockMenu'
import ImageUpload from '@/components/TipTapExtensions/ImageUpload/image-upload'
import ShortcutItem from '@/components/TipTapExtensions/SlashCommand/ShortcutItem'
import SlashCommand from '@/components/TipTapExtensions/SlashCommand/slash-command'
import TiptapTable from '@/components/TipTapExtensions/Table/tiptap-table'
import TiptapTableCell from '@/components/TipTapExtensions/Table/tiptap-table-cell'
import TiptapTableHeader from '@/components/TipTapExtensions/Table/tiptap-table-header'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppSelect from '@/components/ui/AppSelect/AppSelect'

import MentionList from './MentionList'

export default defineComponent({
  name: 'DescriptionField',

  components: {
    AppDivider,
    ImageBlockMenu,
    FormFieldNext,
    ShortcutItem,
    AddGifsDroplist,
    AppButton,
    EditorContent,
    AppSelect,
    DescriptionButton,
    AppIcon,
    AppEmojiPicker,
    AppDroplist,
    AppInput,
    Portal,
    PortalTarget
  },

  provide() {
    return {
      [descriptionFieldStateInjectionKey]: {
        getActiveState: () => this.active,
        tableActionsAppendTo:
          this.tableActionsAppendTo || this.tableActionsAppendToSelectors.selector
      }
    }
  },

  props: {
    modelValue: {
      type: String,
      default: ''
    },

    actions: {
      type: Boolean,
      default: true
    },

    readonly: {
      type: Boolean
    },

    workspaceId: {
      type: [Number, String],
      default: null
    },

    maxLength: {
      type: Number,
      default: DESCRIPTION_FILED_LIMITS.COMMON
    },

    // Note: without placeholder cursor on focused field is invisible in Safari
    placeholder: {
      type: String,
      default: ''
    },

    allowEmpty: {
      type: Boolean,
      default: true
    },

    blurOnSave: {
      type: Boolean,
      default: true
    },

    styled: {
      type: Boolean,
      default: true
    },

    showPlaceholder: {
      type: Boolean,
      default: true
    },

    simpleView: {
      type: Boolean
    },

    disable: {
      type: Boolean
    },

    appendMentionToBody: {
      type: Boolean,
      default: true
    },

    showOnInit: {
      type: Boolean
    },

    placement: {
      type: String,
      default: DESCRIPTION_FIELD_PLACEMENTS
    },

    hiddenFeatures: {
      type: Array,
      default: () => []
    },

    styleType: {
      type: String,
      default: DESCRIPTION_FIELD_TYPES.DEFAULT,
      validator: v => includesValue(v, DESCRIPTION_FIELD_TYPES)
    },

    showCharacterCounter: {
      type: Boolean,
      default: true
    },

    showToolbarDivider: {
      type: Boolean
    },

    moveActionsToToolbar: {
      type: Boolean
    },

    stickyToolbar: {
      type: Boolean,
      default: true
    },

    hardResetOnEmpty: {
      type: Boolean
    },

    shortNameTextFormat: {
      type: Boolean
    },

    tableActionsAppendTo: {
      type: String,
      default: ''
    },

    blurOnEsc: {
      type: Boolean,
      default: true
    }
  },

  emits: {
    'update:modelValue': null,
    cancel: null,
    save: null,
    active: null,
    'characters-limit-exceed': null
  },

  data() {
    return {
      active: false,
      editor: null,
      textFormat: 'paragraph',
      moreFormat: '',
      localValue: this.modelValue,
      editorPopup: null,
      editorComponent: null,
      showUrlModal: false,
      showAddYouTubeModal: false,
      urlText: '',
      url: '',
      showSimpleList: false,
      urlTextError: false,
      urlError: false
    }
  },

  computed: {
    DESCRIPTION_FIELD_TYPES: () => DESCRIPTION_FIELD_TYPES,
    COLORS: () => COLORS,
    ICON_COLORS: () => ICON_COLORS,
    DROP_LIST_THEMES: () => DROP_LIST_THEMES,
    textFormats: () => TEXT_FORMATS,
    TIPTAP_EXTENSION_NAMES: () => TIPTAP_EXTENSION_NAMES,

    uniqActionsUid() {
      return uid()
    },

    tableActionsAppendToSelectors() {
      return createHtmlElementId({
        prefix: 'df-DescriptionField',
        uniqId: this.uniqActionsUid
      })
    },

    isShowCharactersCounter() {
      const CHARACTERS_KEPT_LIMIT = 500
      const limitReached = this.charactersKept > CHARACTERS_KEPT_LIMIT
      if (!this.showCharacterCounter) return false
      if (this.actions) return true
      return !limitReached
    },

    hiddenFeaturesByExternalPlatform() {
      const externalPlatformHiddenFeatures = []

      const isMondayExternalPlatform = this.$store.getters['appContext/isBuiltInMonday']

      if (isMondayExternalPlatform) {
        externalPlatformHiddenFeatures.push(TIPTAP_EXTENSION_NAMES.GIFS)
      }

      return [...this.hiddenFeatures, ...externalPlatformHiddenFeatures]
    },

    editorClasses() {
      return {
        'df-Editor': true,
        'df-Editor-inactive': !this.active,
        'df-Editor-active': this.active,
        'df-Editor-styled': this.styled,
        'df-Editor-readonly': this.readonly
      }
    },

    simpleViewList() {
      return getSimpleViewList({
        editor: this.editor,
        simpleView: this.simpleView,
        showUrlModal: this.showUrlModal,
        hiddenFeatures: this.hiddenFeaturesByExternalPlatform
      })
    },

    isActiveAnyMoreFormats() {
      return !isEmpty(
        this.moreFormats.filter(format => this.editor.isActive(format.value.toLowerCase()))
      )
    },

    moreFormats() {
      return getMoreFormats({ editor: this.editor })
    },

    isDescriptionEmpty() {
      return !(this.editor && !this.editor.isEmpty)
    },

    isDescriptionValid() {
      return this.charactersKept > -1
    },

    isDescriptionTooLong() {
      return this.charactersKept < 0
    },

    charactersKept() {
      // return this.maxLength - this.editor.storage.characterCount.characters()
      return this.maxLength - this.modelValue.length
    },

    getAppendToElement() {
      return () => (this.appendMentionToBody ? document.body : this.$refs.editorRef)
    },

    getAppendToElementClass() {
      return this.appendMentionToBody ? 'body' : '.df-Editor'
    }
  },

  watch: {
    isDescriptionTooLong: {
      handler(newValue) {
        this.$emit('characters-limit-exceed', newValue)
      },

      immediate: true
    },

    active(newValue) {
      this.$emit('active', newValue)
    },

    modelValue(newValue) {
      if (this.localValue !== this.modelValue) {
        this.localValue = newValue
        this.fixMentions()
        // setContent in fixMentions makes editor focused in safari, avoid this
        // this.blur()
      }
    },

    localValue(newValue) {
      if (this.localValue !== this.modelValue) {
        this.$emit('update:modelValue', newValue)
      }
    },

    readonly() {
      this.editor.setEditable(!this.readonly)
    },

    showUrlModal(newValue) {
      if (newValue) {
        const { from, to } = this.editor.state.selection
        /**
         * @link https://prosemirror.net/docs/ref/#model.Node.textBetween
         */
        this.urlText = this.editor.state.doc.textBetween(from, to, ' ')

        this.editor.state.doc.nodesBetween(from, to, node => {
          if (node.isText) {
            // Ищем маркер ссылки среди меток узла
            const linkMark = node.marks.find(mark => mark.type.name === 'link')
            if (!linkMark) {
              // Если текстовый узел не имеет маркера ссылки — прерываем обход
              return false
            } else {
              this.url = linkMark.attrs.href
            }
          }
        })
      } else {
        this.url = ''
        this.urlText = ''
        this.urlTextError = false
        this.urlError = false
      }
    }
  },

  mounted() {
    this.$el.addEventListener('keydown', this.keyListener)
    this.$el.addEventListener('keyup', this.keyListener)
    this.$el.addEventListener('keypress', this.keyListener)
    if (this.blurOnEsc) {
      unshiftKeydownEventListener('esc', this.onEsc)
    }
    this.fixMentions()
    const extensions = [
      StarterKit.configure({
        horizontalRule: false
      }),
      Image,
      ImageBlock,
      ImageUpload.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Alt-k': () =>
              insertBlockOnNewLine({
                editor: this.editor,
                insertBlock: editor => tiptapChainFocusFnc(editor).setImageUpload().run()
              })
          }
        }
      }),
      SlashCommand.configure({
        target: '.df-Editor',
        appendTo: this.getAppendToElement,
        attributes: {
          hiddenFeatures: this.hiddenFeaturesByExternalPlatform,
          data: MENU_LIST,
          commands: {
            [TIPTAP_EXTENSION_NAMES.GIFS]: {
              action: () => this.openGifDroplist()
            },
            [TIPTAP_EXTENSION_NAMES.YOUTUBE]: {
              action: () => this.addYouTubeVideo()
            }
          }
        }
      }),
      HorizontalRule.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Shift-p': () => tiptapChainFocusFnc(this.editor).setHorizontalRule().run()
          }
        }
      }),
      Youtube.configure({
        controls: false,
        nocookie: true,
        width: YOUTUBE_VIDEO_WIDTH,
        height: YOUTUBE_VIDEO_HEIGHT
      }),
      TaskList,
      TaskItem.configure({
        nested: true
      }),
      Link,
      Subscript,
      Underline,
      Superscript.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-;': () => tiptapChainFocusFnc(this.editor).toggleSuperscript().run()
          }
        }
      }),
      TextStyle,
      Color,
      TiptapTable.extend({
        addKeyboardShortcuts() {
          return {
            'Mod-Alt-t': () =>
              insertBlockOnNewLine({
                editor: this.editor,
                insertBlock: editor => tiptapChainFocusFnc(editor).insertTable().run(),
                keepLineBeforeBlock: true
              })
          }
        }
      }),
      TiptapTableHeader,
      TiptapTableCell,
      TableRow,
      CharacterCount.configure({
        limit: this.maxLength
      }),
      Placeholder.configure({
        placeholder: this.placeholder
      })
    ]
    if (this.isHidden(TIPTAP_EXTENSION_NAMES.MENTION)) {
      extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: 'mention'
          },
          renderText({ options, node }) {
            return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
          },
          suggestion: {
            items: async ({ query }) => {
              let users = []
              const usersApi = new UsersApi()

              if (!this.active) return
              try {
                users = await usersApi.getUsersForMentioning({
                  searchString: query,
                  workspaceId: this.workspaceId
                })
                this.addUsersFromList(users)
              } catch (error) {
                handleError({ error })
              }
              return users
            },
            render: () => {
              let component
              let popup

              return {
                onStart: props => {
                  if (!props.clientRect) return
                  const clientRect = props.clientRect()
                  // https://tiptap.dev/examples/suggestions
                  if (
                    clientRect.x === 0 &&
                    clientRect.y === 0 &&
                    clientRect.width === 0 &&
                    clientRect.height === 0
                  ) {
                    // something gone wrong, don't display a popup
                    // it's a case when user deletes mention and also quickly
                    // '@' symbol at the beginning, request for mention suggestions
                    // is sent, but response come after mention onExit lifycecle hook
                    // and mention suggestions are shown, however shouldn't
                    return
                  }
                  component = new VueRenderer(MentionList, {
                    props,
                    editor: props.editor
                  })
                  this.editorComponent = component

                  popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: this.getAppendToElement,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    theme: `${DROP_LIST_THEMES.LIGHT} ${DROP_LIST_THEMES.NO_PADDING}`,
                    arrow: false
                  })
                  this.editorPopup = popup
                },
                onUpdate(props) {
                  if (!props.clientRect) return

                  if (!component) return

                  component.updateProps(props)
                  if (!(popup && popup[0])) {
                    return
                  }

                  if (popup[0].popperInstance) {
                    popup[0].popperInstance.reference = {
                      clientWidth: 0,
                      clientHeight: 0,
                      getBoundingClientRect() {
                        return props.clientRect()
                      }
                    }
                  }
                },
                onKeyDown: props => {
                  return component?.ref?.onKeyDown(props)
                },
                onExit() {
                  if (popup && popup[0]) {
                    popup[0].destroy()
                  }
                  if (component) {
                    component.destroy()
                  }
                  this.editorPopup = null
                  this.editorComponent = null
                }
              }
            }
          }
        })
      )
    }
    this.editor = new Editor({
      content: this.localValue,
      editable: !this.readonly,
      editorProps: {
        attributes: {
          class: 'df-EditorContent'
        }
      },

      extensions,

      // onBlur: ({ event }) => {
      //   if (this.cancelOnBlur) {
      //     if (
      //       Array.from(event.composedPath()).some(item =>
      //         item.classList?.contains('df-DescriptionField')
      //       )
      //     ) {
      //       return
      //     }
      //
      //     this.onCancel()
      //   }
      // },

      onUpdate: ({ editor }) => {
        this.$nextTick(() => {
          if (editor.isEmpty && this.hardResetOnEmpty) {
            this.localValue = ''
            return
          }

          if (editor.isEmpty && isStringEmpty(this.modelValue)) {
            this.localValue = ''
          } else {
            this.localValue = editor.getHTML()
          }
        })
      },

      onTransaction: ({ editor }) => {
        // функция onTransaction отрабатывает каждый раз на изменении чего угодно в редакторе, позиции каретки, добавлении символа и тп.
        // Необходимый ниже функционал нужен для того чтобы определить и подсветить выбранный левел текста в селекте
        const { LEVEL_1, LEVEL_2, LEVEL_3, LEVEL_4, LEVEL_5, LEVEL_6 } = HEADING_LEVELS
        // select value should be updated manually
        const [textFormat] = [
          editor.isActive('heading', { level: LEVEL_1 }) && LEVEL_1,
          editor.isActive('heading', { level: LEVEL_2 }) && LEVEL_2,
          editor.isActive('heading', { level: LEVEL_3 }) && LEVEL_3,
          editor.isActive('heading', { level: LEVEL_4 }) && LEVEL_4,
          editor.isActive('heading', { level: LEVEL_5 }) && LEVEL_5,
          editor.isActive('heading', { level: LEVEL_6 }) && LEVEL_6,
          'paragraph'
        ].filter(Boolean)
        this.textFormat = textFormat
      }
    })
    this.active = this.showOnInit
  },

  beforeUnmount() {
    this.$el.removeEventListener('keydown', this.keyListener)
    this.editor?.destroy()
    removeKeydownEventListener('esc', this.onEsc)
  },

  methods: {
    tiptapChainFocusFnc,
    convertShortcutToText,
    getCurrentTip,
    ...mapActions('objectives', {
      addUsersFromList: 'addUsersFromList',
      getUsersForMentioning: 'getUsersForMentioning'
    }),

    isHidden(featureName) {
      return !this.hiddenFeaturesByExternalPlatform.includes(featureName)
    },

    openGifDroplist() {
      this.$refs.gifDroplistRef.openGifs()
    },

    async replyOnComment(payload) {
      await this.$nextTick()
      if (this.editor) {
        this.editor
          .chain()
          .focus()
          .insertContent({
            type: 'mention',
            attrs: {
              id: payload.accountId,
              label: payload.userName
            }
          })
          .insertContent(' ')
          .run()
      }

      await this.$nextTick()
      this.editor?.commands.focus()
    },

    onClickDescriptionField(e) {
      if (e.target.href) {
        this.editor.commands.blur() // for blur editor when link opening
      } else if (!e.target.href && !this.readonly) {
        this.showEditor()
      }
    },

    execAction(action) {
      this[action]()
    },

    onConfirmSetUrl() {
      this.urlError = isStringEmpty(this.url)
      this.urlTextError = isStringEmpty(this.urlText)

      if (this.urlError || this.urlTextError) {
        return
      }

      let validatedUrl = this.url
      if (!this.url.includes('://')) {
        validatedUrl = 'https://'.concat(this.url)
      }

      this.setLink(validatedUrl)
      this.showUrlModal = false
      this.url = ''
      this.urlText = ''
    },

    onConfirmAddYouTubeUrl() {
      if (isStringEmpty(this.url)) {
        return
      }

      let validatedUrl = this.url
      if (!this.url.includes('://')) {
        validatedUrl = 'https://'.concat(this.url)
      }

      this.setYouTubeLink(validatedUrl)
      this.url = ''
    },

    insertTable() {
      insertBlockOnNewLine({
        editor: this.editor,
        insertBlock: insertTable,
        keepLineBeforeBlock: true
      })
    },

    insertLink() {
      this.showUrlModal = true
    },

    addYouTubeVideo() {
      this.showAddYouTubeModal = true
    },

    setImageUpload() {
      insertBlockOnNewLine({
        editor: this.editor,
        insertBlock: editor => tiptapChainFocusFnc(editor).setImageUpload().run()
      })
    },

    addDivider() {
      tiptapChainFocusFnc(this.editor).setHorizontalRule().run()
    },

    addTaskList() {
      tiptapChainFocusFnc(this.editor).toggleTaskList().run()
    },

    insertCodeSnippet() {
      tiptapChainFocusFnc(this.editor).toggleCode().run()
    },

    insertMention() {
      tiptapChainFocusFnc(this.editor).insertContent('@').run()
    },

    insertQuote() {
      tiptapChainFocusFnc(this.editor).toggleBlockquote().run()
    },

    showEmojiPicker() {
      this.$refs.emojiPickerRef.togglePicker(true)
    },

    getColorAttribute(attribute) {
      return this.editor?.getAttributes(attribute)?.color
    },

    onTextColorChange(value) {
      if (this.getColorAttribute('textStyle') === value) {
        tiptapChainFocusFnc(this.editor).unsetColor().run()
      } else {
        tiptapChainFocusFnc(this.editor).setColor(value).run()
      }
      this.$refs.textColorPicker.hideDropdown()
    },

    showEditor() {
      if (!this.readonly) {
        this.active = true
        this.focus()
      }
    },

    /** @public */
    onCancel() {
      this.onEsc({ focus: false })
      this.$emit('cancel')
      this.active = false
    },

    onSave() {
      this.$emit('save')
      if (this.blurOnSave) {
        this.blur()
      }
    },

    setLink(url) {
      // Извлекаем позицию начала текущего выделения в редакторе
      const { from } = this.editor.state.selection
      // Рассчитываем конечную позицию выделения,
      // добавляя длину текста, который мы собираемся вставить (this.urlText)
      const endTextSelection = from + this.urlText.length

      // Вставляем текст ссылки (this.urlText) в редактор с фокусом на нем.
      // tiptapChainFocusFnc(this.editor) — это вспомогательная функция для создания цепочки команд с установкой фокуса.
      tiptapChainFocusFnc(this.editor).insertContent(this.urlText).run()

      // Устанавливаем выделение текста от позиции 'from' до 'endTextSelection',
      // чтобы выделить только что вставленный текст.
      this.editor.commands.setTextSelection({
        from: from,
        to: endTextSelection
      })

      // Расширяем выделение до границ маркера 'link' и применяем ссылку с указанным URL.
      // Здесь extendMarkRange('link') подбирает весь диапазон, на который уже может быть наложена ссылка,
      // и setLink({ href: url }) устанавливает атрибут ссылки.
      tiptapChainFocusFnc(this.editor).extendMarkRange('link').setLink({ href: url }).run()

      // Сбрасываем выделение: устанавливаем курсор в конец вставленного текста,
      // чтобы больше не было выделения.
      tiptapChainFocusFnc(this.editor)
        .setTextSelection({
          from: endTextSelection,
          to: endTextSelection
        })
        .run()
    },

    setYouTubeLink(url) {
      this.editor.commands.setYoutubeVideo({
        src: url,
        width: YOUTUBE_VIDEO_WIDTH,
        height: YOUTUBE_VIDEO_HEIGHT
      })
      this.showAddYouTubeModal = false
    },

    onMoreFormatChange(option, onSelect) {
      const { value } = option
      onSelect(value)
      if (value === MORE_FORMATS_ACTIONS.CLEAR) {
        tiptapChainFocusFnc(this.editor).clearNodes().unsetAllMarks().run()
      } else {
        if (value === MORE_FORMATS_ACTIONS.SUBSCRIPT && this.editor.isActive('superscript')) {
          tiptapChainFocusFnc(this.editor).unsetSuperscript().run()
        } else if (
          value === MORE_FORMATS_ACTIONS.SUPERSCRIPT &&
          this.editor.isActive('subscript')
        ) {
          tiptapChainFocusFnc(this.editor).unsetSubscript().run()
        }
        tiptapChainFocusFnc(this.editor)[`toggle${value}`]().run()
      }
    },

    insertEmoji(value) {
      this.editor.commands.insertContent(value)
      tiptapChainFocusFnc(this.editor)
    },

    setTextFormat(option, onSelect) {
      const { value } = option
      this.textFormat = value

      onSelect(option)
      if (value === 'paragraph') {
        tiptapChainFocusFnc(this.editor).setParagraph().run()
      }
      tiptapChainFocusFnc(this.editor).toggleHeading({ level: value }).run()
    },

    /** @public */
    focus() {
      this.active = true
      this.$nextTick(() => setTimeout(() => tiptapChainFocusFnc(this.editor).run(), 100))
    },

    /** @public */
    addExternalContent(content = '') {
      if (isStringEmpty(content) || !this.editor) return

      this.editor.chain().focus().insertContent(content).run()
    },

    blur() {
      this.active = false
      this.$nextTick(() =>
        setTimeout(() => {
          this.editor.chain().blur().run()
        }, 100)
      )
    },

    async fixMentions() {
      let content = this.localValue
      const accountIds = getIdsInMentions(content)
      const users = await this.getUsersForMentioning({
        accountIds,
        workspaceId: this.workspaceId
      })
      content = resolveMentions(content, accountIds, users)
      this.editor?.commands.setContent(content, false)
    },

    onEsc() {
      let closed = false
      if (this.editorPopup && this.editorPopup[0]) {
        this.editorPopup[0].destroy()
        closed = true
        this.editorPopup = null
      }
      if (this.editorComponent) {
        this.editorComponent.destroy()
        closed = true
        this.editorComponent = null
      }

      if (!closed) {
        // blur manually instead of blur method because text editor should be blurred
        // only and kept active
        this.editor.commands.blur()
      }

      if (closed) {
        return false
      } else if (this.active) {
        this.$emit('cancel')
        this.active = false
        return false
      }
    },

    keyListener(event) {
      /* Jira Server has own shortcuts, and they raise even in editor. Prevent this behavior. */
      // duplicate keyCode with key, because in keypress(at least in Firefox 93) keyCode is always 0
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }
})
</script>

<style lang="scss" scoped>
// eslint-disable-next-line vue-scoped-css/no-unused-selector
%field-hover {
  &:hover {
    &:not(.df-Editor-readonly &) {
      &:after {
        cursor: text;
        background-color: var(--placeholder-hover-color, $grey-3-next);
      }
    }
  }
  &:not(.df-Editor-active &) {
    &:after {
      display: block;
      content: '';
      position: absolute;
      height: 100%;
      left: -10px;
      top: 0;
      border-radius: $border-radius-sm-next;
      z-index: 1;
      width: calc(100% + 20px);
      pointer-events: none;
    }
  }
}

.df-Editor {
  display: flex;
  flex-direction: column;
  background-color: $white;
  overflow-wrap: break-word;
  border-radius: $border-radius-md-next;

  &-active {
    padding: var(--editor-active-outer-padding, 10px);
    border: var(--editor-active-border, 2px solid #{$grey-3-next});

    .df-DescriptionField-inline & {
      border: none;
    }
  }
}

.df-Content {
  display: flex;
  flex: 1 1 auto;
  height: min-content;
  border-radius: $border-radius-md;
  position: relative;
  cursor: text;

  @extend %field-hover;

  .df-Editor-styled & {
    padding: var(--editor-styled-padding, 12px 0);
  }

  .df-Editor-active & {
    padding: var(--editor-active-padding, 0 10px 15px);
  }

  .df-Editor-inactive & {
    padding: var(--editor-inactive-padding, 0);
  }

  .df-Editor-active:hover & {
    background-color: transparent;
    cursor: initial;
  }
}

.df-ActionsAndCounter {
  display: flex;
  justify-content: space-between;

  &:not(&:empty) {
    margin: 8px 0;
  }
}

.df-Actions {
  display: flex;
  gap: 8px;
  flex-direction: var(--actions-flex-direction, row);
}

.df-Toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
  gap: 7px;
  padding: var(--toolbar-padding, 0);

  background: $white;
  z-index: 3;

  .df-Toolbar-sticky {
    top: 5px;
    position: sticky;
    left: 0;
  }
}

.df-ToolbarSplitter {
  width: 1px;
  height: 24px;
  background-color: $grey-medium;
}

.df-Placeholder {
  color: $dark-3;
  padding: var(--placeholder-padding, 12px 0);
  border-radius: $border-radius-md-next;
  position: relative;
  cursor: text;

  @extend %field-hover;

  &_Text {
    position: relative;
    z-index: 5;
  }
}

.df-DescriptionCounter {
  display: flex;
  align-self: center;
  justify-content: flex-end;
  font-weight: fw('regular');
  font-size: $fs-12;
  line-height: 16px;
  font-family: $system-ui;
  color: $grey-1-next;

  &-negative {
    color: $grade-low-color-next;
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/mixins';

.df-Editor {
  &-readonly {
    [data-type='image-upload'] {
      display: none;
    }
  }
}
.df-EditorContent {
  width: 100%;
  word-break: break-word;
  position: relative;
  z-index: 2;
  &:not(.df-Editor-active &) {
    cursor: text;

    ul[data-type='taskList'] {
      label {
        pointer-events: none;
      }
    }
  }

  ul {
    padding-left: 18px;
    margin-bottom: 0;
  }

  ol {
    padding-left: 16px;
    margin: 0;
  }
  p[data-placeholder] {
    caret-color: $primary-color-next;
    &.is-editor-empty:first-child::before {
      color: $grey-1-next;
    }
  }

  p {
    margin-bottom: 0;
  }

  * + p {
    margin-bottom: 0;
    margin-top: 0.75rem;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: $grey-10;
    pointer-events: none;
    height: 0;
  }

  span.mention {
    @extend %mention-item;
  }
}

.df-DescriptionField {
  margin-bottom: var(--description-field-margin-bottom, 8px);
  font-family: $system-ui;

  .df-TextFormats {
    &:not(&-short) {
      min-width: 125px;
    }

    .df-TextFormats_DroplistButton {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: transparent;
      border-color: transparent;
      min-height: 32px;
      color: $dark-grey;
      font-weight: fw('semi-bold');
      border-radius: 6px;
      padding: 4px 4px 4px 12px;
      font-family: $system-ui;
      cursor: pointer;
      &:hover {
        background-color: $grey-3-next;
      }

      .df-DroplistButton_Text {
        color: $dark-grey;
      }
      .df-DroplistButton_Icon {
        transition: transform $transition-fast ease;
      }
      &-active {
        background: $dark-2;
        color: $white;
        .df-DroplistButton_Icon {
          transform: rotate(180deg);
        }
        &:hover {
          @include hoverState($dark-2);
        }
        .df-DroplistButton_Text {
          color: $white;
        }
      }
      &-focused {
        background: $dark-grey;
        color: $white;

        .as-AppDroplistButton_Text {
          color: $white;
        }

        &:hover {
          border-color: transparent;
          background: $dark-grey;
        }
      }
    }
  }
}

.ProseMirror blockquote,
.ci-Message blockquote {
  padding: 10px 20px 10px 16px;
  border-left: 2px solid $grey-11;
}

.ProseMirror blockquote {
  margin: 14px;
}

.ci-Message blockquote {
  margin: 0 0 4px;
}

.df-ColorTextWrapper {
  position: relative;

  .df-ColorTextWrapper_Marker {
    position: absolute;
    left: 3px;
    right: 0;
    bottom: 3px;
    margin: 0 0 0 2px;
    width: 11px;
    height: 3px;
    border-radius: 3px;
    pointer-events: none;
    background: linear-gradient(to right, #172b4d, #36b27e, #0052cc, #f64963);
  }
}

.df-ColorTextWrapper_OptionWrapper {
  border: 2px solid transparent;
  margin: 0;
  display: flex;
  align-items: center;
  border-radius: 10px;

  &:hover {
    border-color: $grey-2-next;
  }
}

.df-ColorTextWrapper_Option {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $black;
  height: 22px;
  width: 22px;
  border-radius: $border-radius-md;
  border: 1px solid rgba(23, 43, 77, 0.12);
  cursor: pointer;
}

.df-ColorText_ContentOptions {
  .as-UngroupedItems {
    display: flex;
    flex-wrap: wrap;
    padding: 20px;
    gap: 2px;
    justify-content: space-between;
  }
}

.tableWrapper {
  overflow-x: auto;
}

.df-TextFormats_Heading {
  padding: 12px 10px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  overflow: hidden;
  max-width: 100%;

  &:hover,
  &-active {
    background-color: rgba($primary-color-next, 0.15);
  }

  &-disabled {
    pointer-events: none;
    background-color: transparent;
    color: $grey-semi-medium;

    &:active {
      cursor: not-allowed;
    }
  }

  &-paragraph {
    @include setHeading($fs-14, 20px, fw('regular'));
  }

  &-1 {
    @include setHeading($fs-24, 28px);
  }

  &-2 {
    @include setHeading($fs-20, 28px);
  }

  &-3 {
    @include setHeading($fs-18, 20px);
  }

  &-4 {
    @include setHeading($fs-16, 20px);
  }

  &-5 {
    @include setHeading($fs-14, 20px);
  }

  &-6 {
    @include setHeading($fs-12, 16px, fw('bold'));
    color: $grey-semi-medium;
  }
}

.df-SimpleViewList {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  overflow: hidden;
}

.df-SimpleViewList_Icon {
  flex-shrink: 0;
}

.df-Toolbar_LinkWrapper {
  padding: 20px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 16px;
}

.df-LinkWrapper {
  &_Input {
    min-width: 460px;
  }
}

.df-EditorActions {
  margin: 0 0 0 auto;
}
</style>

<style lang="scss">
@import '~@/assets/styles/tiptap-styles';
</style>
